import { Injectable } from "@angular/core";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { Municipality } from "app/addresses/store/municipalities/municipalities.model";
import { catchError, tap } from "rxjs/operators";
import { PublicMemberSearchCommandService } from "./public-member-search-command.service";
import { PublicMemberSearchQueryService } from "./public-member-search-query.service";
import { DownloadDecisionDocument, FetchMemberPublicInfos, FetchMemberPublicInfosFailure, FetchMemberPublicInfosSuccess, LoadAdministrativeRegions, LoadAdministrativeRegionsFailure, LoadAdministrativeRegionsSuccess, LoadInsuranceStatusCodes, LoadInsuranceStatusCodesFailure, LoadInsuranceStatusCodesSuccess, LoadSpecialities, LoadSpecialitiesFailure, LoadSpecialitiesSuccess, SearchMunicipality, SearchMunicipalityFailure, SearchMunicipalitySuccess, SearchPublicMemberBy, SearchPublicMemberByFailure, SearchPublicMemberBySuccess } from "./public-member-search.actions";
import { AdministrativeRegion, InsuranceStatusCode, PublicMember, PublicMemberInfos, PublicMemberSearchCriterias, SpecialtyType } from "./public-member-search.model";

export class PublicMemberSearchStateModel {
  specialities: SpecialtyType[];
  municipalities: Municipality[];
  members: PublicMember[];
  insuranceStatusCodes: InsuranceStatusCode[];
  memberPublicInfos: PublicMemberInfos;
  memberPublicInfosLoading = false;
  administrativeRegions: AdministrativeRegion[];
  searching = false;
  selectedCriterias: PublicMemberSearchCriterias
}

@State<PublicMemberSearchStateModel>({
  name: 'publicMemberSearch',
  defaults: new PublicMemberSearchStateModel(),
})
@Injectable()
export class PublicMemberSearchState {

  @Selector()
  static specialities(state: PublicMemberSearchStateModel): SpecialtyType[] {
    return state.specialities || []
  }
  @Selector()
  static municipalities(state: PublicMemberSearchStateModel): Municipality[] {
    return state.municipalities || []
  }
  @Selector()
  static members(state: PublicMemberSearchStateModel): PublicMember[] {
    return state.members || []
  }
  @Selector()
  static memberPublicInfos(state: PublicMemberSearchStateModel): PublicMemberInfos {
    return state.memberPublicInfos
  }
  @Selector()
  static memberPublicInfosLoading(state: PublicMemberSearchStateModel): boolean {
    return state.memberPublicInfosLoading
  }

  @Selector()
  static administrativeRegions(state: PublicMemberSearchStateModel): AdministrativeRegion[] {
    return state.administrativeRegions || []
  }

  @Selector()
  static searching(state: PublicMemberSearchStateModel): boolean {
    return state.searching
  }

  @Selector()
  static insuranceStatusCodes(state: PublicMemberSearchStateModel): InsuranceStatusCode[] {
    return state.insuranceStatusCodes || []
  }

  constructor(private publicMemberSearchQueryService: PublicMemberSearchQueryService, private publicMemberSearchCommandService: PublicMemberSearchCommandService) { }

  @Action(LoadSpecialities)
  onLoadSpecialities(
    { dispatch }: StateContext<PublicMemberSearchStateModel>,
  ) {
    return this.publicMemberSearchQueryService.getAllSpecialities().pipe(
      tap(res => dispatch(new LoadSpecialitiesSuccess(res))),
      catchError(err => dispatch(new LoadSpecialitiesFailure(err)))
    )
  }

  @Action(LoadSpecialitiesSuccess)
  onLoadSpecialitiesSuccess(
    { patchState }: StateContext<PublicMemberSearchStateModel>,
    { specialities }: LoadSpecialitiesSuccess
  ) {
    patchState({ specialities });
  }

  @Action(SearchMunicipality)
  onSearchMunicipality(
    { dispatch }: StateContext<PublicMemberSearchStateModel>,
    { serchTerm }: SearchMunicipality
  ) {
    return this.publicMemberSearchQueryService.searchMunicipalities(serchTerm).pipe(
      tap(res => dispatch(new SearchMunicipalitySuccess(res))),
      catchError(err => dispatch(new SearchMunicipalityFailure(err)))
    )
  }

  @Action(SearchMunicipalitySuccess)
  onSearchMunicipalitySuccess(
    { patchState }: StateContext<PublicMemberSearchStateModel>,
    { municipalities }: SearchMunicipalitySuccess
  ) {
    patchState({ municipalities });
  }

  @Action(SearchPublicMemberBy)
  onSearchPublicMemberBy(
    { dispatch, patchState }: StateContext<PublicMemberSearchStateModel>,
    { cityName, specialtyTypeId, administrativeRegionCode, isAnesthesiaHospital, isAnesthesiaPrivateClinic, isParticularMedicalCondition
      , isHomeVisit, isSleepApnea, isReducedMobility, isLiftPerson
    }: SearchPublicMemberBy
  ) {
    const selectedCriterias = {
      cityName,
      specialtyTypeId,
      administrativeRegionCode
    } as PublicMemberSearchCriterias;
    patchState({ searching: true, selectedCriterias });
    return this.publicMemberSearchQueryService.searchMemberBy(cityName, specialtyTypeId, administrativeRegionCode, isAnesthesiaHospital, isAnesthesiaPrivateClinic, isParticularMedicalCondition, isHomeVisit, isSleepApnea, isReducedMobility, isLiftPerson).pipe(
      tap(res => dispatch(new SearchPublicMemberBySuccess(res, selectedCriterias))),
      catchError(err => dispatch(new SearchPublicMemberByFailure(err)))
    )
  }

  @Action(SearchPublicMemberBySuccess)
  onSearchPublicMemberBySuccess(
    { patchState, getState }: StateContext<PublicMemberSearchStateModel>,
    { members, criterias }: SearchPublicMemberBySuccess
  ) {
    const selectedCriterias = getState().selectedCriterias;

    if (criterias.cityName === selectedCriterias.cityName &&
      criterias.specialtyTypeId === selectedCriterias.specialtyTypeId &&
      criterias.administrativeRegionCode === selectedCriterias.administrativeRegionCode) {

      patchState({ members, searching: false });
    }

  }

  @Action(FetchMemberPublicInfos)
  onFetchMemberPublicInfos(
    { dispatch, patchState }: StateContext<PublicMemberSearchStateModel>,
    { memberId }: FetchMemberPublicInfos
  ) {
    patchState({ memberPublicInfosLoading: true })
    return this.publicMemberSearchQueryService.fetchPublicMemberInfos(memberId).pipe(
      tap(res => dispatch(new FetchMemberPublicInfosSuccess(res))),
      catchError(err => dispatch(new FetchMemberPublicInfosFailure(err)))
    )
  }

  @Action(FetchMemberPublicInfosSuccess)
  onFetchMemberPublicInfosSuccess(
    { patchState }: StateContext<PublicMemberSearchStateModel>,
    { memberPublicInfos }: FetchMemberPublicInfosSuccess
  ) {
    patchState({ memberPublicInfos, memberPublicInfosLoading: false });
  }

  @Action(LoadAdministrativeRegions)
  onLoadAdministrativeRegions(
    { dispatch }: StateContext<PublicMemberSearchStateModel>,
  ) {
    return this.publicMemberSearchQueryService.getAllAdministrativeRegions().pipe(
      tap(res => dispatch(new LoadAdministrativeRegionsSuccess(res))),
      catchError(err => dispatch(new LoadAdministrativeRegionsFailure(err)))
    )
  }

  @Action(LoadAdministrativeRegionsSuccess)
  onLoadAdministrativeRegionsSuccess(
    { patchState }: StateContext<PublicMemberSearchStateModel>,
    { administrativeRegions }: LoadAdministrativeRegionsSuccess
  ) {
    patchState({ administrativeRegions });
  }

  @Action(DownloadDecisionDocument)
  onDownloadDecisionDocument(
    { dispatch }: StateContext<PublicMemberSearchStateModel>,
    { document }: DownloadDecisionDocument
  ) {
    return this.publicMemberSearchCommandService.authorizeDocumentDownload(document.decisionId, document.documentId, document.documentName);
  }

  @Action(LoadInsuranceStatusCodes)
  onLoadInsuranceStatusCodes(
    { dispatch }: StateContext<PublicMemberSearchStateModel>,
  ) {
    return this.publicMemberSearchQueryService.getAllInsuranceStatusCodes().pipe(
      tap(res => dispatch(new LoadInsuranceStatusCodesSuccess(res))),
      catchError(err => dispatch(new LoadInsuranceStatusCodesFailure(err)))
    )
  }

  @Action(LoadInsuranceStatusCodesSuccess)
  onLoadInsuranceStatusCodesSuccess(
    { patchState }: StateContext<PublicMemberSearchStateModel>,
    { insuranceStatusCodes }: LoadInsuranceStatusCodesSuccess
  ) {
    patchState({ insuranceStatusCodes });
  }
}
